import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const options = {
  desk: [
    { id: 'd1', name: 'Horizon Basic', price: 399, desc: 'Single motor, 150lb limit', detailImg: 'https://images.unsplash.com/photo-1593640408182-31c70c8268f5?auto=format&fit=crop&q=80&w=600' },
    { id: 'd2', name: 'Horizon Pro', price: 699, desc: 'Dual motor, 300lb limit', detailImg: 'https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?auto=format&fit=crop&q=80&w=600' }
  ],
  chair: [
    { id: 'c1', name: 'SpineAlign Task', price: 450, desc: 'Mesh back, lumbar focus', detailImg: 'https://images.unsplash.com/photo-1505843490538-5133c6c7d0e1?auto=format&fit=crop&q=80&w=600' },
    { id: 'c2', name: 'SpineAlign Exec', price: 850, desc: 'Full leather, 4D arms', detailImg: 'https://images.unsplash.com/photo-1589182397057-b82d0059b841?auto=format&fit=crop&q=80&w=600' }
  ],
  cable: [
    { id: 'cb1', name: 'None', price: 0, desc: 'Manual routing only', detailImg: 'https://images.unsplash.com/photo-1591488320449-011701bb6704?auto=format&fit=crop&q=80&w=600' },
    { id: 'cb2', name: 'Stealth Tray', price: 45, desc: 'Steel under-desk housing', detailImg: 'https://images.unsplash.com/photo-1591488320449-011701bb6704?auto=format&fit=crop&q=80&w=600' }
  ]
};

const BuildSetup = () => {
  const [selection, setSelection] = useState({ desk: 'd2', chair: 'c1', cable: 'cb2' });
  const [activeDetail, setActiveDetail] = useState('desk');

  const updateSelection = (category, id) => {
    setSelection(prev => ({ ...prev, [category]: id }));
    setActiveDetail(category);
  };

  const getPrice = (category, id) => options[category].find(opt => opt.id === id)?.price || 0;
  const getName = (category, id) => options[category].find(opt => opt.id === id)?.name || '';
  const getDetailImg = (category, id) => options[category].find(opt => opt.id === id)?.detailImg || '';

  const total = getPrice('desk', selection.desk) + getPrice('chair', selection.chair) + getPrice('cable', selection.cable);

  return (
    <section className="py-24 bg-white border-y border-slate-200 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:row gap-12 lg:items-stretch">
          <div className="lg:w-1/3 flex flex-col">
            <div className="mb-8">
              <div className="mono-label text-indigo-600 mb-2">Build Module v4.1</div>
              <h2 className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">Configure Your Core.</h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                Workspace performance begins at the physical layer. Select your primary hardware components to initialize your setup.
              </p>
            </div>

            <div className="space-y-5 flex-grow">
              {Object.keys(options).map((category) => (
                <div key={category} className="group">
                  <div className="flex items-center justify-between mb-2">
                    <span className="mono-label text-slate-400">{category} selection</span>
                    <SafeIcon icon={FiIcons.FiCheckCircle} className={selection[category] ? 'text-indigo-500' : 'text-slate-200'} />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {options[category].map(opt => (
                      <button 
                        key={opt.id} 
                        onClick={() => updateSelection(category, opt.id)}
                        className={`flex items-start justify-between p-4 border rounded-xl text-left transition-all relative ${
                          selection[category] === opt.id ? 'border-indigo-600 bg-indigo-50/30 ring-1 ring-indigo-600' : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <div>
                          <div className={`font-bold text-sm ${selection[category] === opt.id ? 'text-indigo-900' : 'text-slate-800'}`}>
                            {opt.name}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</div>
                        </div>
                        <span className={`font-mono text-xs font-bold ${selection[category] === opt.id ? 'text-indigo-600' : 'text-slate-400'}`}>
                          +${opt.price}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:w-2/3 flex flex-col min-h-[500px]">
            <div className="bg-slate-900 rounded-2xl p-1 border border-slate-800 shadow-2xl flex flex-col h-full relative overflow-hidden group">
              <div className="bg-slate-800/80 backdrop-blur rounded-t-xl p-3.5 flex items-center justify-between border-b border-slate-700/50">
                <div className="flex gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                </div>
                <div className="text-[10px] font-mono text-slate-400 tracking-widest flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiCpu} className="animate-pulse" />
                  PREVIEWING_CONFIGURATION.SYS
                </div>
                <div className="w-10"></div>
              </div>

              <div className="flex-grow relative flex items-center justify-center p-8 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[linear-gradient(rgba(79,70,229,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(79,70,229,0.1)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={JSON.stringify(selection)}
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 1.05, y: -10 }}
                    className="relative z-10 w-full max-w-lg"
                  >
                    <div className="relative border-2 border-indigo-500/40 bg-indigo-500/5 p-6 rounded-2xl backdrop-blur-md shadow-[0_0_50px_-12px_rgba(79,70,229,0.3)] mb-6 overflow-hidden">
                      <div className="relative z-10 text-center">
                        <div className="mono-label text-indigo-400 mb-2">Component Detail</div>
                        <div className="text-white text-3xl font-black tracking-tight uppercase mb-4">
                          {getName(activeDetail, selection[activeDetail])}
                        </div>
                      </div>
                      
                      {/* Contextual Image Integration */}
                      <div className="relative h-48 rounded-xl overflow-hidden border border-slate-700 mb-4 bg-slate-800">
                        <picture>
                          <source type="image/avif" srcSet={`${getDetailImg(activeDetail, selection[activeDetail])}&fm=avif&w=600`} />
                          <source type="image/webp" srcSet={`${getDetailImg(activeDetail, selection[activeDetail])}&fm=webp&w=600`} />
                          <img 
                            src={getDetailImg(activeDetail, selection[activeDetail])} 
                            alt={`Contextual detail of ${getName(activeDetail, selection[activeDetail])} hardware interaction`}
                            className="w-full h-full object-cover opacity-60 mix-blend-luminosity hover:opacity-100 transition-opacity duration-500"
                            loading="lazy"
                            decoding="async"
                            width="600"
                            height="400"
                          />
                        </picture>
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                      </div>

                      <div className="flex justify-center gap-3 relative z-10">
                        <span className="px-2 py-1 bg-indigo-500/10 rounded border border-indigo-500/20 text-[10px] text-indigo-300 font-mono uppercase">Modular_Link_Active</span>
                        <span className="px-2 py-1 bg-indigo-500/10 rounded border border-indigo-500/20 text-[10px] text-indigo-300 font-mono uppercase">Ergo_Validated</span>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
                
                <div className="absolute bottom-4 right-4 text-[10px] font-mono text-slate-700">
                  REF: DH-CORE-CONFIG-X7
                </div>
              </div>

              <div className="bg-white p-6 rounded-b-xl border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest mb-1">Investment Total</div>
                  <div className="text-4xl font-black text-slate-900 font-mono tracking-tighter">
                    <span className="text-slate-300 text-2xl mr-1">$</span>{total}
                  </div>
                </div>
                <button className="w-full md:w-auto px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-xl shadow-indigo-100 transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-3">
                  <SafeIcon icon={FiIcons.FiShoppingCart} className="text-xl" />
                  Deploy Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuildSetup;